import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import './PlayerProfilePage.css';
import PlayerFace from '../components/PlayerFace';
import PlaystyleTab from './PlaystyleTab';
import DevelopmentTab from './DevelopmentTab';
import { getPlayerById, generateBuyPortraits } from '../services/db';
import { SUPABASE_ANON_KEY, FUNCTIONS_URL } from '../services/supabaseConfig';
import { LEAGUE_TYPE, QL_ARRAY, TRAD_ARRAY } from '../constants/leagueTypes';
import { numberToGrade, getGradeColor, getAttrColor, getOverallColor } from '../utils/grades';

interface GameLogEntry {
  game_day: number;
  is_pitcher: boolean;
  plate_appearances: number;
  at_bats: number;
  runs: number;
  hits: number;
  doubles: number;
  triples: number;
  home_runs: number;
  rbi: number;
  walks: number;
  strikeouts: number;
  stolen_bases: number;
  innings_pitched: number;
  hits_allowed: number;
  runs_allowed: number;
  earned_runs: number;
  walks_allowed: number;
  strikeouts_pitched: number;
  home_runs_allowed: number;
  win: boolean;
  loss: boolean;
  save: boolean;
  pitches_thrown: number;
}

interface PlayerProfileProps {
  playerId?: string;
  onBack?: () => void;
  ballsBalance?: number;
  onDeductBalls?: (amount: number, type?: string, desc?: string, refId?: string) => Promise<boolean>;
  currentUserId?: string;
  onRetire?: (playerId: string) => Promise<boolean>;
}

type TabType = 'stats' | 'attributes' | 'playstyle' | 'equipment' | 'awards' | 'bloodline' | 'contracts' | 'development';

interface EquipOption {
  attr: string;
  label: string;
  icon: string;
}

const BATTER_OPTIONS: EquipOption[] = [
  { attr: 'power', label: 'Power', icon: '💪' },
  { attr: 'contact', label: 'Contact', icon: '🎯' },
  { attr: 'speed', label: 'Speed', icon: '⚡' },
  { attr: 'fielding', label: 'Fielding', icon: '🧤' },
  { attr: 'arm', label: 'Arm', icon: '💪' },
  { attr: 'discipline', label: 'Discipline', icon: '🎓' },
  { attr: 'endurance', label: 'Endurance', icon: '🏃' },
];

const PITCHER_OPTIONS: EquipOption[] = [
  { attr: 'velocity', label: 'Velocity', icon: '🔥' },
  { attr: 'control', label: 'Control', icon: '🎯' },
  { attr: 'movement', label: 'Movement', icon: '🌊' },
  { attr: 'deception', label: 'Deception', icon: '🎭' },
  { attr: 'endurance', label: 'Endurance', icon: '🏃' },
];

const BOOST_TIERS = [
  { boost: 3, cost: 500, label: '+3', itemId: '' },
  { boost: 5, cost: 1000, label: '+5', itemId: '' },
  { boost: 8, cost: 1500, label: '+8', itemId: '' },
];

const SLOT_INFO: Record<string, { icon: string; label: string }> = {
  hat: { icon: '🧢', label: 'Hat' },
  glove: { icon: '🧤', label: 'Glove' },
  sunglasses: { icon: '🕶️', label: 'Sunglasses' },
};

const PlayerProfilePage: React.FC<PlayerProfileProps> = ({ playerId, onBack, ballsBalance = 0, onDeductBalls, currentUserId, onRetire }) => {
  const [player, setPlayer] = useState<any>(null);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState<TabType>('stats');
  const [showGameLog, setShowGameLog] = useState(false);
  const [equipModal, setEquipModal] = useState<{ slot: string } | null>(null);
  const [retiring, setRetiring] = useState(false);
  const [retireMsg, setRetireMsg] = useState('');
  const [buying, setBuying] = useState(false);
  const [buyMsg, setBuyMsg] = useState('');
  const [sellModal, setSellModal] = useState(false);
  const [sellPrice, setSellPrice] = useState('');
  const [sellLoading, setSellLoading] = useState(false);
  const [sellMsg, setSellMsg] = useState('');
  const [studModal, setStudModal] = useState(false);
  const [studFee, setStudFee] = useState('5000');
  const [studPublic, setStudPublic] = useState(true);
  const [studLoading, setStudLoading] = useState(false);
  const [studMsg, setStudMsg] = useState('');
  const [contractOffers, setContractOffers] = useState<any[]>([]);
  const [assignTeams, setAssignTeams] = useState<any[]>([]);
  const [assignSelectedTeam, setAssignSelectedTeam] = useState<string>('');
  const [renaming, setRenaming] = useState(false);
  const [renameName, setRenameName] = useState('');
  const [assignLoading, setAssignLoading] = useState(false);
  const [assignMsg, setAssignMsg] = useState('');

  // Post-buy flow state
  const [postBuyStep, setPostBuyStep] = useState<'rename' | 'portraits' | 'done' | null>(null);
  const [postBuyPlayerData, setPostBuyPlayerData] = useState<any>(null);
  const [renameFirst, setRenameFirst] = useState('');
  const [renameLast, setRenameLast] = useState('');
  const [renameSaving, setRenameSaving] = useState(false);
  const [portraitChoices, setPortraitChoices] = useState<{ slot: number; url: string }[]>([]);
  const [portraitLoading, setPortraitLoading] = useState(false);
  const [portraitError, setPortraitError] = useState('');
  const [selectedPortrait, setSelectedPortrait] = useState<number | null>(null);
  const [portraitSaving, setPortraitSaving] = useState(false);
  const [loadingOffers, setLoadingOffers] = useState(false);
  const params = useParams();
  const navigate = useNavigate();
  const actualPlayerId = playerId || params.playerId;

  useEffect(() => {
    async function fetchPlayer() {
      if (!actualPlayerId) return;
      setLoading(true);
      try {
        const fetchedPlayer = await getPlayerById(actualPlayerId);
        if (fetchedPlayer) {
          // Load season stats and game logs
          const { supabase, isSupabaseConfigured } = await import('../services/supabase');
          if (isSupabaseConfigured() && supabase) {
            const [seasonRes, gameLogRes, teamRes] = await Promise.all([
              supabase.from('player_season_stats').select('*').eq('player_id', actualPlayerId).order('season_year', { ascending: true }),
              supabase.from('player_game_stats').select('*').eq('player_id', actualPlayerId).order('game_day', { ascending: false }).limit(20),
              supabase.from('teams').select('id, name, city').eq('id', fetchedPlayer.teamId || (fetchedPlayer as any).team_id).single()
            ]);
            const stats = seasonRes.data || [];
            // Add team name to each season stat
            if (teamRes.data) {
              stats.forEach((s: any) => { s.team_name = teamRes.data.name; s.team_id = teamRes.data.id; });
            }
            (fetchedPlayer as any).allSeasonStats = stats;
            (fetchedPlayer as any).gameLogs = gameLogRes.data || [];
            (fetchedPlayer as any).teamName = teamRes.data?.name || '';
            (fetchedPlayer as any).team = teamRes.data?.name || '';
          }
        }
        setPlayer(fetchedPlayer);
      } catch (error) {
        console.error('Failed to fetch player:', error);
      } finally {
        setLoading(false);
      }
    }
    fetchPlayer();
  }, [actualPlayerId]);

  // Load contract offers for this player
  useEffect(() => {
    async function loadOffers() {
      if (!actualPlayerId) return;
      setLoadingOffers(true);
      try {
        const { supabase, isSupabaseConfigured } = await import('../services/supabase');
        if (isSupabaseConfigured() && supabase) {
          const { data } = await supabase
            .from('contract_offers')
            .select('*')
            .eq('player_id', actualPlayerId)
            .eq('status', 'pending')
            .order('created_at', { ascending: false });
          setContractOffers(data || []);
        }
      } catch (err) {
        console.error('Failed to load offers:', err);
      } finally {
        setLoadingOffers(false);
      }
    }
    loadOffers();
  }, [actualPlayerId]);

  // Load teams for assignment when player is a free agent (with or without a league)
  useEffect(() => {
    async function loadAssignTeams() {
      if (!player || (player as any).team_id) return; // Only for FAs
      const { supabase, isSupabaseConfigured } = await import('../services/supabase');
      if (!isSupabaseConfigured() || !supabase) return;
      const { data: { session } } = await supabase.auth.getSession();
      if (!session) return;

      const playerLeagueId = (player as any).league_id;
      if (playerLeagueId) {
        // Player has a league — show all teams in that league
        const { data: teams } = await supabase
          .from('teams')
          .select('id, name, city, is_ai, owner_id, league_id, leagues(name, season_year)')
          .eq('league_id', playerLeagueId)
          .order('name');
        setAssignTeams(teams || []);
      } else {
        // Player has no league (e.g. newly hatched) — show user's traditional-league teams only
        // First get QL league IDs to exclude them
        const { data: qlLeagues } = await supabase
          .from('leagues')
          .select('id')
          .in('league_type', QL_ARRAY as readonly string[]);
        const qlIds = (qlLeagues || []).map((l: any) => l.id);

        let q = supabase
          .from('teams')
          .select('id, name, city, is_ai, owner_id, league_id, leagues(name, season_year)')
          .eq('owner_id', session.user.id);
        if (qlIds.length > 0) {
          q = q.not('league_id', 'in', `(${qlIds.join(',')})`);
        }
        const { data: teams } = await q.order('name');
        setAssignTeams(teams || []);
      }
    }
    loadAssignTeams();
  }, [player]);

  if (loading) {
    return (
      <div className="player-profile-page">
        <div className="loading-container">
          <div className="spinner" />
          <p>Loading player...</p>
        </div>
      </div>
    );
  }

  if (!player) {
    return (
      <div className="player-profile-page">
        <div className="error-container">
          <p>Player not found.</p>
          {onBack && <button className="back-btn" onClick={onBack}>← Back</button>}
        </div>
      </div>
    );
  }

  // Normalize player name - handle both {name} and {firstName, lastName} formats
  const playerName = player.name || `${player.firstName || player.first_name || ''} ${player.lastName || player.last_name || ''}`.trim() || 'Unknown';
  // Position is now just 'P' for all pitchers
  const playerPosition = player.position || 'Unknown';
  // Check if pitcher
  const isPitcherPos = (pos: string) => {
    const p = (pos || '').toUpperCase();
    return p === 'P';
  };
  const isPitcher = isPitcherPos(playerPosition);
  // Normalize attributes - handle both {attributes: {...}} and flat format
  const attrs = player.attributes || {
    power: player.power,
    contact: player.contact,
    speed: player.speed,
    fielding: player.fielding,
    arm: player.arm,
    discipline: player.discipline,
    endurance: player.endurance,
    velocity: player.velocity,
    control: player.control,
    movement: player.movement,
    deception: player.deception,
  };

  const batting = player.seasonBatting;
  const pitching = player.seasonPitching;
  const gameLogs = player.gameLogs || [];

  const handleRename = async () => {
    if (!renameName.trim()) return;
    const nameParts = renameName.trim().split(' ');
    const firstName = nameParts[0];
    const lastName = nameParts.slice(1).join(' ') || '';
    
    const { supabase: sb } = await import('../services/supabase');
    const { error } = await sb
      .from('players')
      .update({ first_name: firstName, last_name: lastName })
      .eq('id', player.id);
    
    if (error) {
      console.error('Rename failed:', error);
      return;
    }
    
    setPlayer({ ...player, first_name: firstName, last_name: lastName, firstName, lastName });
    setRenaming(false);
  };

  const handleRetire = async () => {
    if (retiring) return;
    const careerSeason = player.careerSeason || (player as any).career_season || 0;
    if (careerSeason < 1) {
      setRetireMsg(`Must complete at least 1 season (currently ${careerSeason}).`);
      return;
    }
    if (!window.confirm(`Are you sure you want to retire ${playerName}? This cannot be undone.`)) return;
    setRetiring(true);
    try {
      const { supabase, isSupabaseConfigured } = await import('../services/supabase');
      if (!isSupabaseConfigured() || !supabase) {
        setRetireMsg('Not connected to database.');
        return;
      }

      const teamId = player.teamId;

      // Retire the player — set retired=true, clear team_id
      const { error } = await supabase
        .from('players')
        .update({ retired: true, team_id: null })
        .eq('id', player.id);
      if (error) {
        setRetireMsg('Retirement failed: ' + error.message);
        return;
      }

      // Remove from team's batting_order and rotation_order
      if (teamId) {
        const { data: team } = await supabase
          .from('teams')
          .select('id, batting_order, rotation_order')
          .eq('id', teamId)
          .single();
        if (team) {
          const updates: Record<string, any> = {};
          if (team.batting_order && Array.isArray(team.batting_order)) {
            const newBo = team.batting_order.filter((id: string) => id !== player.id);
            if (newBo.length !== team.batting_order.length) updates.batting_order = newBo;
          }
          if (team.rotation_order && Array.isArray(team.rotation_order)) {
            const newRo = team.rotation_order.filter((id: string) => id !== player.id);
            if (newRo.length !== team.rotation_order.length) updates.rotation_order = newRo;
          }
          if (Object.keys(updates).length > 0) {
            await supabase.from('teams').update(updates).eq('id', teamId);
          }
        }
      }

      player.retired = true;
      player.teamId = undefined;
      (player as any).team = '';
      setPlayer({ ...player });
      setRetireMsg(`${playerName} has retired. 👴`);
    } catch (e: any) {
      setRetireMsg('Retirement failed: ' + (e.message || 'Unknown error'));
    } finally {
      setRetiring(false);
    }
  };

  const handleListStud = async () => {
    const fee = parseInt(studFee, 10);
    if (isNaN(fee) || fee < 1000) { setStudMsg('Minimum fee is 1,000 $BALLS'); return; }
    setStudLoading(true);
    setStudMsg('');
    try {
      const { supabase: sb } = await import('../services/supabase');
      const { data: { session } } = await sb.auth.getSession();
      if (!session) { setStudMsg('Not authenticated'); return; }
      const res = await fetch(`${FUNCTIONS_URL}/forge`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${session.access_token}` },
        body: JSON.stringify({ action: 'list-stud', playerId: player.id, fee, isPublic: studPublic }),
      });
      const data = await res.json();
      if (data.success || res.ok) {
        setStudMsg(`✅ ${playerName} listed as stud for ${fee.toLocaleString()} $BALLS`);
        setStudModal(false);
      } else {
        setStudMsg('Failed: ' + (data.error || 'Unknown error'));
      }
    } catch (e: any) {
      setStudMsg('Failed: ' + (e.message || 'Unknown error'));
    } finally {
      setStudLoading(false);
    }
  };

  const isOwner = currentUserId && player.userId && currentUserId === player.userId;

  const handleSell = async () => {
    if (sellLoading) return;
    const price = parseInt(sellPrice);
    if (!price || price < 1) {
      setSellMsg('Price must be at least 1 $BALLS');
      return;
    }
    if (player.non_tradeable) {
      setSellMsg('Free players cannot be sold. Forge them first.');
      return;
    }
    setSellLoading(true);
    setSellMsg('');
    try {
      const { supabase: sb } = await import('../services/supabase');
      const { data: { session } } = await sb.auth.getSession();
      if (!session) { setSellMsg('Not authenticated'); return; }

      const res = await fetch(`${FUNCTIONS_URL}/marketplace`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${session.access_token}`,
        },
        body: JSON.stringify({
          action: 'list',
          playerId: player.id,
          sellerId: currentUserId,
          price,
        }),
      });
      const data = await res.json();
      if (res.ok && data.status === 'success') {
        setSellMsg(`Listed for ${price.toLocaleString()} $BALLS! 5% fee on sale.`);
        setSellModal(false);
        (player as any).listedForSale = true;
        setPlayer({ ...player });
      } else {
        setSellMsg(data.error || 'Failed to list player');
      }
    } catch (e: any) {
      setSellMsg('Failed: ' + (e.message || 'Unknown error'));
    } finally {
      setSellLoading(false);
    }
  };
  const isUnowned = !player.userId && !player.retired && player.teamId;
  const playerOvr = player.overall || 0;
  const buyPrice = 3000; // Flat price for all AI players

  const handleBuyRights = async () => {
    if (buying || !currentUserId) return;
    if (!window.confirm(`Buy rights to ${playerName} for ${buyPrice.toLocaleString()} $BALLS? Player stays on their team.`)) return;
    setBuying(true);
    setBuyMsg('');
    try {
      const { supabase: sb } = await import('../services/supabase');
      const { data: { session } } = await sb.auth.getSession();
      if (!session) { setBuyMsg('Please sign in first'); return; }
      const res = await fetch(`${FUNCTIONS_URL}/buy-ai-player`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${session.access_token}` },
        body: JSON.stringify({ action: 'buy', playerId: player.id }),
      });
      const data = await res.json();
      if (res.ok && !data.error) {
        (player as any).userId = currentUserId;
        setPlayer({ ...player });
        // Open post-buy flow
        setPostBuyPlayerData({
          ...data.player,
          originalFirst: player.firstName,
          originalLast: player.lastName,
        });
        setRenameFirst('');
        setRenameLast('');
        setPostBuyStep('rename');
      } else {
        setBuyMsg(data.error || 'Purchase failed');
      }
    } catch (e: any) {
      setBuyMsg(e.message || 'Purchase failed');
    } finally {
      setBuying(false);
    }
  };

  const handlePostBuyRename = async () => {
    if (!postBuyPlayerData) return;
    setRenameSaving(true);
    try {
      const firstName = renameFirst.trim() || postBuyPlayerData.first_name;
      const lastName = renameLast.trim() || postBuyPlayerData.last_name;
      postBuyPlayerData.first_name = firstName;
      postBuyPlayerData.last_name = lastName;

      setPostBuyStep('portraits');
      setPortraitLoading(true);
      setPortraitError('');
      setPortraitChoices([]);

      // Use Reve Remix client-side with NFT parent images
      const p1 = postBuyPlayerData.nft_parent1_url || (player as any).nft_parent1_url;
      const p2 = postBuyPlayerData.nft_parent2_url || (player as any).nft_parent2_url;

      if (!p1 || !p2) {
        setPortraitError('No NFT parent images available for this player.');
        return;
      }

      const [url1, url2] = await generateBuyPortraits(p1, p2, `${firstName} ${lastName}`, postBuyPlayerData.position, postBuyPlayerData.gender || 'male');
      setPortraitChoices([{ slot: 1, url: url1 }, { slot: 2, url: url2 }]);
    } catch (e: any) {
      setPortraitError('Portrait generation failed: ' + (e.message || 'Unknown error'));
    } finally {
      setPortraitLoading(false);
      setRenameSaving(false);
    }
  };

  const handleForgePortrait = async () => {
    if (!currentUserId || portraitLoading) return;
    setPortraitLoading(true);
    setPortraitError('');
    setPortraitChoices([]);
    setSelectedPortrait(null);
    try {
      const p1 = (player as any).nft_parent1_url;
      const p2 = (player as any).nft_parent2_url;
      if (!p1 || !p2) {
        setPortraitError('No NFT parent images — portrait generation requires gen0 NFT bloodline.');
        setPortraitLoading(false);
        return;
      }

      const pName = `${player.firstName || (player as any).first_name || ''} ${player.lastName || (player as any).last_name || ''}`.trim();
      setPostBuyPlayerData({
        id: player.id,
        first_name: (player as any).first_name || player.firstName || '',
        last_name: (player as any).last_name || player.lastName || '',
        position: playerPosition,
        gender: player.gender || 'male',
        nft_parent1_url: p1,
        nft_parent2_url: p2,
      });
      setPostBuyStep('portraits');

      // Reve Remix client-side — blends 2 gen0 NFT parent images
      const [url1, url2] = await generateBuyPortraits(p1, p2, pName, playerPosition, player.gender || 'male');
      setPortraitChoices([{ slot: 1, url: url1 }, { slot: 2, url: url2 }]);
    } catch (e: any) {
      setPortraitError('Portrait generation failed: ' + (e.message || 'Unknown error'));
      setPostBuyStep(null);
    } finally {
      setPortraitLoading(false);
    }
  };

  const handleSelectPortrait = async (slot: number) => {
    if (!postBuyPlayerData) return;
    setSelectedPortrait(slot);
    setPortraitSaving(true);
    try {
      const { supabase: sb } = await import('../services/supabase');
      const { data: { session } } = await sb.auth.getSession();
      if (!session) return;

      // Get the URL for the chosen portrait (slot 1 or 2)
      const chosen = portraitChoices.find(c => c.slot === slot);
      if (!chosen) return;

      // Upload via buy-player confirm (saves portrait + sets avatar_url)
      const res = await fetch(`${FUNCTIONS_URL}/buy-player`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${session.access_token}` },
        body: JSON.stringify({
          action: 'confirm',
          playerId: postBuyPlayerData.id,
          firstName: postBuyPlayerData.first_name,
          lastName: postBuyPlayerData.last_name,
          portraitUrl: chosen.url,
        }),
      });
      const data = await res.json();
      if (data.success) {
        setPlayer((prev: any) => ({ ...prev, avatarUrl: data.player.avatar_url }));
      } else {
        console.error('buy-player confirm failed:', data.error);
      }
    } catch (e) {
      console.error('handleSelectPortrait error:', e);
    } finally {
      setPortraitSaving(false);
      setPostBuyStep('done');
    }
  };

  const closePostBuy = () => {
    setPostBuyStep(null);
    setPostBuyPlayerData(null);
    setPortraitChoices([]);
    setPortraitError('');
    setSelectedPortrait(null);
    // Refresh player name if renamed
    if (postBuyPlayerData?.first_name) {
      setPlayer((prev: any) => ({
        ...prev,
        firstName: postBuyPlayerData.first_name,
        lastName: postBuyPlayerData.last_name,
      }));
    }
  };
  
  const handleAvatarUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file || !currentUserId) return;
    if (file.size > 512000) { alert('File too large. Max 500KB.'); return; }
    if (!['image/jpeg', 'image/png', 'image/webp'].includes(file.type)) { alert('Use JPG, PNG, or WebP.'); return; }
    
    const formData = new FormData();
    formData.append('file', file);
    formData.append('userId', currentUserId);
    formData.append('targetType', 'player');
    formData.append('targetId', player.id);
    
    try {
      const { supabase: sb } = await import('../services/supabase');
      const { data: { session } } = await sb.auth.getSession();
      const res = await fetch(`${FUNCTIONS_URL}/upload-avatar`, {
        method: 'POST', body: formData,
        headers: { 'Authorization': `Bearer ${session?.access_token}` },
      });
      const data = await res.json();
      if (data.url) {
        (player as any).avatarUrl = data.url;
        setPlayer({ ...player });
      } else {
        alert(data.error || 'Upload failed');
      }
    } catch (err) {
      alert('Upload failed');
    }
  };
  
  const handleReport = async () => {
    if (!currentUserId) return;
    const reason = prompt('Why are you reporting this? (inappropriate image, etc.)');
    if (!reason) return;
    try {
      const { supabase: sb } = await import('../services/supabase');
      const { data: { session } } = await sb.auth.getSession();
      const res = await fetch(`${FUNCTIONS_URL}/report-content`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${session?.access_token}`, 'Content-Type': 'application/json' },
        body: JSON.stringify({ action: 'submit', reporterId: currentUserId, targetType: 'player', targetId: player.id, reason }),
      });
      const data = await res.json();
      if (data.autoHidden) alert('Reported. Image has been hidden due to multiple reports.');
      else alert('Reported. We will review it.');
    } catch { alert('Report failed'); }
  };

  return (
    <div className="player-profile-page">
      <button className="back-btn" onClick={onBack}>← Back</button>
      
      {/* ===== HEADER — PLAYER CARD ===== */}
      <div className="card-front">
        <div className="card-portrait">
          {(player as any).avatarUrl ? (
            <img src={(player as any).avatarUrl} alt={playerName} className="portrait-avatar" />
          ) : player.face ? (
            <PlayerFace face={player.face} size={140} />
          ) : (
            <div className="portrait-circle">
              {playerName.split(' ').map((n: string) => n[0]).join('').slice(0, 2)}
            </div>
          )}

        </div>
        <div className="card-info">
          {renaming ? (
            <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
              <input
                type="text"
                value={renameName}
                onChange={e => setRenameName(e.target.value)}
                onKeyDown={async e => {
                  if (e.key === 'Enter' && renameName.trim()) {
                    await handleRename();
                  }
                  if (e.key === 'Escape') setRenaming(false);
                }}
                autoFocus
                placeholder={playerName}
                style={{
                  background: '#0d1117', border: '1px solid #d4a017', borderRadius: 8,
                  padding: '8px 12px', color: '#f0ede6', fontSize: 20, fontWeight: 700,
                  outline: 'none', width: 220,
                }}
              />
              <button
                onClick={handleRename}
                disabled={!renameName.trim()}
                style={{
                  background: 'linear-gradient(135deg, #d4a017, #e8b82a)', color: '#060a12',
                  border: 'none', borderRadius: 8, padding: '8px 16px', fontSize: 14, fontWeight: 700,
                  cursor: renameName.trim() ? 'pointer' : 'not-allowed', opacity: renameName.trim() ? 1 : 0.5,
                }}
              >Save</button>
              <button
                onClick={() => setRenaming(false)}
                style={{ background: '#21262d', color: '#8b949e', border: 'none', borderRadius: 8, padding: '8px 12px', fontSize: 14, cursor: 'pointer' }}
              >✕</button>
            </div>
          ) : (
            <h1
              className="card-name"
              onClick={() => {
                setRenameName(playerName);
                setRenaming(true);
              }}
              style={{ cursor: 'pointer' }}
              title="Click to rename"
            >
              {playerName}
              <span className={`gender-badge ${player.gender === 'female' ? 'female' : 'male'}`}>
                {player.gender === 'female' ? '♀' : '♂'}
              </span>
            </h1>
          )}
          <div className="card-meta-row">
            <span className="card-position">{playerPosition}</span>
            {player.teamId ? (
              <span className="card-team card-team-link" onClick={() => {
                window.dispatchEvent(new CustomEvent('navigate', { detail: `/team/${player.teamId}` }));
              }}>{player.team}</span>
            ) : (
              <span className="card-team card-team-free">Free Agent</span>
            )}
          </div>
          <div className="card-bio">
            <span className={`gender-badge ${player.gender === 'female' ? 'female' : 'male'}`}>
              {player.gender === 'female' ? '♀' : '♂'}
            </span>
            <span className="dot">·</span>
            <span>{Math.floor(player.height / 12)}'{player.height % 12}"</span>
            <span className="dot">·</span>
            <span>{player.weight} lbs</span>
            <span className="dot">·</span>
            <span>Age {player.age}</span>
            <span className="dot">·</span>
            <span>Bats {player.battingHand}</span>
            <span className="dot">·</span>
            <span>Throws {player.throwingHand}</span>
          </div>
          <div className="card-overall">
            <div className="ovr-number" style={{ color: getAttrColor(player.overall) }}>{numberToGrade(player.overall)}</div>
            <div className="ovr-label">OVR</div>
          </div>
        </div>
      </div>

      {/* Stamina bar */}
      {player.current_stamina !== undefined && player.current_stamina !== null && (
        <div className="stamina-bar-wrapper">
          <span className="stamina-label">Stamina</span>
          <div className="stamina-track">
            <div className="stamina-fill" style={{
              width: `${player.current_stamina}%`,
              background: player.current_stamina >= 60 ? '#00d4aa' : player.current_stamina >= 35 ? '#f59e0b' : '#ef4444'
            }} />
          </div>
          <span className="stamina-val">{player.current_stamina}%</span>
        </div>
      )}

      {/* Buy Rights button — for unowned AI players on teams */}
      {isUnowned && currentUserId && (
        <div className="retire-section">
          <button className="buy-rights-btn" onClick={handleBuyRights} disabled={buying}>
            {buying ? 'Buying...' : `🏷️ Buy Rights — ${buyPrice.toLocaleString()} $BALLS`}
          </button>
          {buyMsg && <span className="retire-msg">{buyMsg}</span>}
        </div>
      )}

      {/* Retire + Sell buttons — only for owner's players */}
      {isOwner && !player.retired && (() => {
        const cs = player.careerSeason || (player as any).career_season || 0;
        const eligible = cs >= 1;
        const canSell = !player.non_tradeable && !(player as any).listedForSale;
        return (
          <div className="owner-actions">
            <button className="retire-btn" onClick={handleRetire} disabled={retiring || !eligible}>
              {retiring ? 'Retiring...' : eligible ? '👴 Retire Player' : `👴 Retire (Season ${cs}/1)`}
            </button>
            {canSell && (
              <button className="sell-btn" onClick={() => { setSellModal(true); setSellPrice(''); setSellMsg(''); }}>
                💰 Sell on Market
              </button>
            )}
            {(player as any).listedForSale && (
              <span className="sell-listed-badge">🏷️ Listed for Sale</span>
            )}
            {player.non_tradeable && (
              <span className="sell-listed-badge" style={{color:'#888'}}>🔒 Non-tradeable</span>
            )}
            {retireMsg && <span className="retire-msg">{retireMsg}</span>}
            {sellMsg && <span className="retire-msg sell-msg">{sellMsg}</span>}
          </div>
        );
      })()}
      {/* Retired players can still be listed on marketplace */}
      {isOwner && player.retired && (() => {
        const canSell = !player.non_tradeable && !(player as any).listedForSale;
        const isMale = (player.gender || (player as any).gender) === 'male';
        const noPortrait = !(player as any).avatarUrl;
        return (
          <div className="owner-actions">
            <span className="retire-msg retired-badge">👴 Retired</span>
            {noPortrait && (
              <button className="buy-rights-btn" onClick={() => handleForgePortrait()} disabled={portraitLoading}>
                {portraitLoading ? 'Forging...' : '🔨 Forge Portrait'}
              </button>
            )}
            {isMale && !player.non_tradeable && (
              <button className="sell-btn" onClick={() => { setStudModal(true); setStudMsg(''); }} style={{ background: 'linear-gradient(135deg, #7c3aed, #a855f7)' }}>
                ⚡ List as Stud
              </button>
            )}
            {!isMale && (
              <button className="sell-btn" onClick={() => navigate('/forge', { state: { femalePlayerId: player.id } })} style={{ background: 'linear-gradient(135deg, #be185d, #ec4899)' }}>
                🧬 Forge
              </button>
            )}
            {studMsg && <span className="retire-msg sell-msg" style={{ color: studMsg.startsWith('✅') ? '#22c55e' : '#ef4444' }}>{studMsg}</span>}
            {canSell && (
              <button className="sell-btn" onClick={() => { setSellModal(true); setSellPrice(''); setSellMsg(''); }}>
                💰 Sell on Market
              </button>
            )}
            {(player as any).listedForSale && (
              <span className="sell-listed-badge">🏷️ Listed for Sale</span>
            )}
            {sellMsg && <span className="retire-msg sell-msg">{sellMsg}</span>}
            {portraitError && <span className="retire-msg sell-msg" style={{ color: '#ef4444' }}>{portraitError}</span>}
          </div>
        );
      })()}

      {/* Forge Portrait — for owned active players missing a portrait */}
      {isOwner && !player.retired && !(player as any).avatarUrl && (
        <div className="owner-actions" style={{ marginTop: 8 }}>
          <button className="buy-rights-btn" onClick={() => handleForgePortrait()} disabled={portraitLoading}>
            {portraitLoading ? 'Forging...' : '🔨 Forge Portrait'}
          </button>
          {portraitError && <span className="retire-msg sell-msg" style={{ color: '#ef4444' }}>{portraitError}</span>}
        </div>
      )}

      {/* Sell modal */}
      {sellModal && (
        <div className="bap-modal-overlay" onClick={() => setSellModal(false)}>
          <div className="bap-modal bap-postbuy-modal" onClick={e => e.stopPropagation()}>
            <h3 className="bap-modal-title">💰 List on Marketplace</h3>
            <p className="bap-postbuy-desc">
              Set your asking price for <strong>{player.firstName} {player.lastName}</strong> (OVR {numberToGrade(player.overall)} {player.position}).
              A 5% marketplace fee is deducted when sold.
            </p>
            <div className="bap-rename-fields">
              <div className="bap-rename-field">
                <label>Price ($BALLS)</label>
                <input
                  className="bap-rename-input"
                  type="number"
                  min={1}
                  step={100}
                  placeholder="e.g. 5000"
                  value={sellPrice}
                  onChange={e => setSellPrice(e.target.value)}
                />
              </div>
            </div>
            {sellPrice && parseInt(sellPrice) >= 100 && (
              <div className="sell-fee-info">
                You receive: <strong>{Math.floor(parseInt(sellPrice) * 0.95).toLocaleString()}</strong> $BALLS (after 5% fee)
              </div>
            )}
            <div className="bap-modal-buttons">
              <button className="bap-modal-cancel" onClick={() => setSellModal(false)}>Cancel</button>
              <button className="bap-modal-confirm" disabled={sellLoading || !sellPrice} onClick={handleSell}>
                {sellLoading ? 'Listing...' : 'List for Sale'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ===== STUD LISTING MODAL ===== */}
      {studModal && (
        <div className="bap-modal-overlay" onClick={() => setStudModal(false)}>
          <div className="bap-modal bap-postbuy-modal" onClick={e => e.stopPropagation()}>
            <h3 className="bap-modal-title">⚡ List as Forge Stud</h3>
            <p className="bap-postbuy-desc">
              List <strong>{playerName}</strong> (OVR {numberToGrade(player.overall)} {player.position}) as a stud on the Forge market.
              Other players pay your fee to breed with him.
            </p>
            <div className="bap-rename-fields">
              <div className="bap-rename-field">
                <label>Breeding Fee ($BALLS)</label>
                <input
                  className="bap-rename-input"
                  type="number"
                  min={1000}
                  step={500}
                  placeholder="e.g. 5000"
                  value={studFee}
                  onChange={e => setStudFee(e.target.value)}
                />
              </div>
              <div className="bap-rename-field" style={{ marginTop: 12 }}>
                <label style={{ display: 'flex', alignItems: 'center', gap: 8, cursor: 'pointer' }}>
                  <input
                    type="checkbox"
                    checked={studPublic}
                    onChange={e => setStudPublic(e.target.checked)}
                    style={{ width: 16, height: 16 }}
                  />
                  Public listing (visible to all players)
                </label>
              </div>
            </div>
            {studMsg && <div style={{ color: '#ef4444', fontSize: 13, marginTop: 8 }}>{studMsg}</div>}
            <div className="bap-modal-buttons">
              <button className="bap-modal-cancel" onClick={() => setStudModal(false)}>Cancel</button>
              <button
                className="bap-modal-confirm"
                disabled={studLoading || !studFee || parseInt(studFee) < 1000}
                onClick={handleListStud}
                style={{ background: 'linear-gradient(135deg, #7c3aed, #a855f7)' }}
              >
                {studLoading ? 'Listing...' : '⚡ List Stud'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ===== TABS ===== */}
      <div className="profile-tabs">
        <button className={`tab-btn ${activeTab === 'stats' ? 'active' : ''}`} onClick={() => setActiveTab('stats')}>Stats</button>
        <button className={`tab-btn ${activeTab === 'attributes' ? 'active' : ''}`} onClick={() => setActiveTab('attributes')}>Attributes</button>
        <button className={`tab-btn ${activeTab === 'playstyle' ? 'active' : ''}`} onClick={() => setActiveTab('playstyle')}>🎚️ Playstyle</button>
        <button className={`tab-btn ${activeTab === 'equipment' ? 'active' : ''}`} onClick={() => setActiveTab('equipment')}>Equipment</button>
        <button className={`tab-btn ${activeTab === 'awards' ? 'active' : ''}`} onClick={() => setActiveTab('awards')}>Awards</button>
        <button className={`tab-btn ${activeTab === 'bloodline' ? 'active' : ''}`} onClick={() => setActiveTab('bloodline')}>🩸 Bloodline</button>
        <button className={`tab-btn ${activeTab === 'development' ? 'active' : ''}`} onClick={() => setActiveTab('development')}>📈 Development</button>
        {(contractOffers.length > 0 || (!(player as any).team_id && (player as any).league_id)) && (
          <button className={`tab-btn ${activeTab === 'contracts' ? 'active' : ''}`} onClick={() => setActiveTab('contracts')}>
            📋 {contractOffers.length > 0 ? `Contracts (${contractOffers.length})` : 'Assign Team'}
          </button>
        )}
      </div>

      {/* ===== TAB CONTENT ===== */}
      <div className="tab-content">

        {/* ===== STATS TAB ===== */}
        {activeTab === 'stats' && (
          <div className="stats-tab">
            {isPitcher ? (
              /* ===== PITCHER — YEARLY STATS ===== */
              <>
                {/* Yearly pitching table */}
                {player.allSeasonStats && player.allSeasonStats.filter((s: any) => s.is_pitcher !== false).length > 0 ? (
                  <div className="stat-table-section bbref-section">
                    <h4 className="section-title">Standard Pitching</h4>
                    <div className="bbref-scroll">
                      <table className="bbref-table">
                        <thead>
                          <tr>
                            <th>Year</th>
                            <th>Team</th>
                            <th>W</th><th>L</th><th>ERA</th><th>G</th><th>GS</th>
                            <th>IP</th><th>H</th><th>ER</th>
                            <th>BB</th><th>K</th><th>WHIP</th>
                          </tr>
                        </thead>
                        <tbody>
                          {player.allSeasonStats.filter((s: any) => s.is_pitcher !== false).map((s: any, i: number) => {
                            const ip = parseFloat(s.innings_pitched) || 0;
                            const h = s.hits_allowed || 0;
                            const er = s.earned_runs || 0;
                            const bb = s.walks_allowed || 0;
                            const k = s.strikeouts_pitched || 0;
                            const w = s.wins || 0;
                            const l = s.losses || 0;
                            const era = ip > 0 ? (er * 9) / ip : 0;
                            const whip = ip > 0 ? (bb + h) / ip : 0;
                            return (
                              <tr key={i}>
                                <td>{s.season_year}</td>
                                <td>{s.team_id ? <span className="team-link" onClick={() => navigate(`/team/${s.team_id}`)}>{s.team_name || '—'}</span> : (s.team_name || '—')}</td>
                                <td>{w}</td>
                                <td>{l}</td>
                                <td className="stat-highlight">{era.toFixed(2)}</td>
                                <td>{s.games || 0}</td>
                                <td>{s.starts || 0}</td>
                                <td>{ip.toFixed(1)}</td>
                                <td>{h}</td>
                                <td>{er}</td>
                                <td>{bb}</td>
                                <td>{k}</td>
                                <td>{whip.toFixed(2)}</td>
                              </tr>
                            );
                          })}
                          {/* Career totals row */}
                          {player.allSeasonStats.length > 0 && (() => {
                            const pitcherStats = player.allSeasonStats.filter((s: any) => s.is_pitcher !== false);
                            const totals = pitcherStats.reduce((acc: any, s: any) => {
                              acc.w += s.wins || 0;
                              acc.l += s.losses || 0;
                              acc.g += s.games || 0;
                              acc.gs += s.starts || 0;
                              acc.ip += parseFloat(s.innings_pitched) || 0;
                              acc.h += s.hits_allowed || 0;
                              acc.er += s.earned_runs || 0;
                              acc.bb += s.walks_allowed || 0;
                              acc.k += s.strikeouts_pitched || 0;
                              return acc;
                            }, { w: 0, l: 0, g: 0, gs: 0, ip: 0, h: 0, er: 0, bb: 0, k: 0 });
                            const era = totals.ip > 0 ? (totals.er * 9) / totals.ip : 0;
                            const whip = totals.ip > 0 ? (totals.bb + totals.h) / totals.ip : 0;
                            return (
                              <tr className="totals-row" style={{fontWeight:'bold',background:'#1a2535'}}>
                                <td>Total</td>
                                <td>—</td>
                                <td>{totals.w}</td><td>{totals.l}</td>
                                <td className="stat-highlight">{era.toFixed(2)}</td>
                                <td>{totals.g}</td>
                                <td>{totals.gs}</td>
                                <td>{totals.ip.toFixed(1)}</td>
                                <td>{totals.h}</td><td>{totals.er}</td>
                                <td>{totals.bb}</td><td>{totals.k}</td>
                                <td>{whip.toFixed(2)}</td>
                              </tr>
                            );
                          })()}
                        </tbody>
                      </table>
                    </div>
                  </div>
                ) : pitching ? (
                  <div className="stat-table-section bbref-section">
                    <h4 className="section-title">{pitching.year} Season — Pitching</h4>
                    <div className="bbref-scroll">
                      <table className="bbref-table">
                        <thead>
                          <tr>
                            <th>Season</th><th>Age</th><th>Team</th><th>Lg</th>
                            <th>W</th><th>L</th><th>W-L%</th>
                            <th>ERA</th><th>G</th><th>GS</th><th>IP</th>
                            <th>H</th><th>R</th><th>ER</th><th>HR</th>
                            <th>BB</th><th>K</th><th>K/9</th><th>BB/9</th><th>K/BB</th>
                          </tr>
                        </thead>
                        <tbody>
                          <tr>
                            <td className="bbref-season">{pitching.year}</td>
                            <td className="bbref-age">{player.age || '—'}</td>
                            <td className="bbref-team">{player.team || '—'}</td>
                            <td className="bbref-lg">—</td>
                            <td>{pitching.wins}</td>
                            <td>{pitching.losses}</td>
                            <td>{(pitching.wins + pitching.losses) > 0 ? (pitching.wins / (pitching.wins + pitching.losses)).toFixed(3).replace(/^0/, '') : '.000'}</td>
                            <td className="bbref-highlight">{pitching.era.toFixed(2)}</td>
                            <td>{pitching.games || 0}</td>
                            <td>{(pitching as any).starts || 0}</td>
                            <td>{pitching.innings.toFixed(1)}</td>
                            <td>{pitching.hits}</td>
                            <td>{pitching.runs}</td>
                            <td>{pitching.earnedRuns}</td>
                            <td>{pitching.hr}</td>
                            <td>{pitching.walks}</td>
                            <td className="bbref-highlight">{pitching.strikeouts}</td>
                            <td>{pitching.so9.toFixed(1)}</td>
                            <td>{pitching.bb9.toFixed(1)}</td>
                            <td>{pitching.soBb.toFixed(2)}</td>
                          </tr>
                        </tbody>
                      </table>
                    </div>
                  </div>
                ) : (
                  <p className="no-data">No pitching stats yet this season.</p>
                )}

                {/* Pitcher Ratings */}
                <div className="stat-table-section">
                  <h4 className="section-title">Ratings</h4>
                  <table className="ratings-table">
                    <thead>
                      <tr>
                        {['Velocity', 'Control', 'Movement', 'Stamina'].map(label => (
                          <th key={label}>{label}</th>
                        ))}
                      </tr>
                    </thead>
                    <tbody>
                      <tr>
                        {['velocity', 'control', 'movement', 'stamina'].map(attr => {
                          const val = player.attributes?.[attr] || 0;
                          return (
                            <td key={attr}>
                              <span className="grade-cell" style={{ color: getAttrColor(val) }}>
                                {numberToGrade(val)}
                              </span>
                            </td>
                          );
                        })}
                      </tr>
                    </tbody>
                  </table>
                </div>
              </>
            ) : (
              <>
                {player.allSeasonStats && player.allSeasonStats.length > 0 ? (
                  <div className="stat-table-section bbref-section">
                    <h4 className="section-title">Standard Batting</h4>
                    <div className="bbref-scroll">
                      <table className="bbref-table">
                        <thead>
                          <tr>
                            <th>Year</th>
                            <th>Team</th>
                            <th>G</th><th>AB</th><th>H</th>
                            <th>HR</th><th>RBI</th><th>R</th>
                            <th>BB</th><th>K</th><th>SB</th>
                            <th>AVG</th><th>OBP</th><th>SLG</th><th>OPS</th>
                          </tr>
                        </thead>
                        <tbody>
                          {player.allSeasonStats.filter((s: any) => !s.is_pitcher).map((s: any, i: number) => {
                            const ab = s.at_bats || 0;
                            const h = s.hits || 0;
                            const bb = s.walks || 0;
                            const singles = h - (s.doubles||0) - (s.triples||0) - (s.home_runs||0);
                            const tb = singles + (s.doubles||0)*2 + (s.triples||0)*3 + (s.home_runs||0)*4;
                            const avg = ab > 0 ? h/ab : 0;
                            const hbp = s.hit_by_pitch || 0;
                            const sf = s.sacrifice_flies || 0;
                            const obpDenom = ab + bb + hbp + sf;
                            const obp = obpDenom > 0 ? (h + bb + hbp) / obpDenom : 0;
                            const slg = ab > 0 ? tb/ab : 0;
                            const ops = obp + slg;
                            return (
                              <tr key={i}>
                                <td>{s.season_year}</td>
                                <td>{s.team_id ? <span className="team-link" onClick={() => navigate(`/team/${s.team_id}`)}>{s.team_name || '—'}</span> : (s.team_name || '—')}</td>
                                <td>{s.games||0}</td>
                                <td>{ab}</td>
                                <td>{h}</td>
                                <td>{s.home_runs||0}</td>
                                <td>{s.rbi||0}</td>
                                <td>{s.runs||0}</td>
                                <td>{bb}</td>
                                <td>{s.strikeouts||0}</td>
                                <td>{s.stolen_bases||0}</td>
                                <td className="stat-highlight">{avg.toFixed(3).replace(/^0/,'')}</td>
                                <td className="stat-highlight">{obp.toFixed(3).replace(/^0/,'')}</td>
                                <td className="stat-highlight">{slg.toFixed(3).replace(/^0/,'')}</td>
                                <td className="stat-highlight">{ops.toFixed(3).replace(/^0/,'')}</td>
                              </tr>
                            );
                          })}
                          {/* Career totals row */}
                          {player.allSeasonStats.length > 0 && (() => {
                            const batterStats = player.allSeasonStats.filter((s: any) => !s.is_pitcher);
                            const totals = batterStats.reduce((acc: any, s: any) => {
                              acc.g += s.games || 0;
                              acc.ab += s.at_bats || 0;
                              acc.r += s.runs || 0;
                              acc.h += s.hits || 0;
                              acc.doubles += s.doubles || 0;
                              acc.triples += s.triples || 0;
                              acc.hr += s.home_runs || 0;
                              acc.rbi += s.rbi || 0;
                              acc.sb += s.stolen_bases || 0;
                              acc.bb += s.walks || 0;
                              acc.k += s.strikeouts || 0;
                              acc.hbp += s.hit_by_pitch || 0;
                              acc.sf += s.sacrifice_flies || 0;
                              return acc;
                            }, { g: 0, ab: 0, r: 0, h: 0, doubles: 0, triples: 0, hr: 0, rbi: 0, sb: 0, bb: 0, k: 0, hbp: 0, sf: 0 });
                            const singles = totals.h - totals.doubles - totals.triples - totals.hr;
                            const tb = singles + totals.doubles*2 + totals.triples*3 + totals.hr*4;
                            const avg = totals.ab > 0 ? totals.h / totals.ab : 0;
                            const obpDenom = totals.ab + totals.bb + totals.hbp + totals.sf;
                            const obp = obpDenom > 0 ? (totals.h + totals.bb + totals.hbp) / obpDenom : 0;
                            const slg = totals.ab > 0 ? tb / totals.ab : 0;
                            return (
                              <tr style={{fontWeight:'bold',background:'#1a2535'}}>
                                <td>Total</td>
                                <td>—</td>
                                <td>{totals.g}</td><td>{totals.ab}</td><td>{totals.h}</td>
                                <td>{totals.hr}</td><td>{totals.rbi}</td><td>{totals.r}</td>
                                <td>{totals.bb}</td><td>{totals.k}</td><td>{totals.sb}</td>
                                <td className="stat-highlight">{avg.toFixed(3).replace(/^0/,'')}</td>
                                <td className="stat-highlight">{obp.toFixed(3).replace(/^0/,'')}</td>
                                <td className="stat-highlight">{slg.toFixed(3).replace(/^0/,'')}</td>
                                <td className="stat-highlight">{(obp+slg).toFixed(3).replace(/^0/,'')}</td>
                              </tr>
                            );
                          })()}
                        </tbody>
                      </table>
                    </div>
                  </div>
                ) : batting ? (
                  /* Single season fallback */
                  <div className="stat-table-section bbref-section">
                    <h4 className="section-title">{batting.year} Season — Batting</h4>
                    <div className="bbref-scroll">
                      <table className="bbref-table">
                        <thead>
                          <tr>
                            <th>Season</th><th>Age</th><th>Team</th><th>Lg</th>
                            <th>G</th><th>PA</th><th>AB</th>
                            <th>R</th><th>H</th><th>2B</th><th>3B</th>
                            <th>HR</th><th>RBI</th><th>SB</th>
                            <th>BB</th><th>K</th>
                            <th>BA</th><th>OBP</th><th>SLG</th><th>OPS</th>
                          </tr>
                        </thead>
                        <tbody>
                          <tr>
                            <td className="bbref-season">{batting.year}</td>
                            <td className="bbref-age">{player.age || '—'}</td>
                            <td className="bbref-team">{player.team || '—'}</td>
                            <td className="bbref-lg">—</td>
                            <td>{batting.gamesPlayed}</td>
                            <td>{batting.pa}</td>
                            <td>{batting.ab}</td>
                            <td>{batting.runs}</td>
                            <td>{batting.hits}</td>
                            <td>{batting.doubles}</td>
                            <td>{batting.triples}</td>
                            <td className="bbref-highlight">{batting.homeRuns}</td>
                            <td>{batting.rbi}</td>
                            <td>{batting.stolenBases}</td>
                            <td>{batting.walks}</td>
                            <td>{batting.strikeouts}</td>
                            <td className="bbref-highlight">{batting.avg.toFixed(3).replace(/^0/, '')}</td>
                            <td>{batting.obp.toFixed(3).replace(/^0/, '')}</td>
                            <td>{batting.slg.toFixed(3).replace(/^0/, '')}</td>
                            <td className="bbref-highlight">{batting.ops.toFixed(3).replace(/^0/, '')}</td>
                          </tr>
                        </tbody>
                      </table>
                    </div>
                  </div>
                ) : (
                  <p className="no-data">No batting stats yet this season.</p>
                )}

                {/* Hitter Ratings */}
                <div className="stat-table-section">
                  <h4 className="section-title">Ratings</h4>
                  <table className="ratings-table">
                    <thead>
                      <tr>
                        {['Power', 'Contact', 'Speed', 'Fielding', 'Arm', 'Discipline'].map(label => (
                          <th key={label}>{label}</th>
                        ))}
                      </tr>
                    </thead>
                    <tbody>
                      <tr>
                        {['power', 'contact', 'speed', 'fielding', 'arm', 'discipline'].map(attr => {
                          const val = player.attributes?.[attr] || 0;
                          return (
                            <td key={attr}>
                              <span className="grade-cell" style={{ color: getAttrColor(val) }}>
                                {numberToGrade(val)}
                              </span>
                            </td>
                          );
                        })}
                      </tr>
                    </tbody>
                  </table>
                </div>
              </>
            )}

            {/* ===== GAME LOG TABLE (collapsed by default) ===== */}
            {gameLogs.length > 0 && (
              <div className="stat-table-section game-log-section">
                <div className="game-log-header">
                  <button className="game-log-toggle" onClick={() => setShowGameLog(!showGameLog)}>
                    <span className="game-log-arrow">{showGameLog ? '▾' : '▸'}</span>
                    Game Log ({gameLogs.length} games)
                  </button>
                </div>
                {showGameLog && (
                  <div className="game-log-wrapper">
                    <table className="game-log-table">
                      <thead>
                        <tr>
                          <th>Day</th>
                          {isPitcher ? (
                            <>
                              <th>IP</th><th>H</th><th>R</th><th>ER</th>
                              <th>BB</th><th>K</th><th>HR</th><th>Pit</th><th>Dec</th>
                            </>
                          ) : (
                            <>
                              <th>AB</th><th>R</th><th>H</th><th>2B</th>
                              <th>3B</th><th>HR</th><th>RBI</th><th>BB</th>
                              <th>K</th><th>SB</th>
                            </>
                          )}
                        </tr>
                      </thead>
                      <tbody>
                        {gameLogs.map((log: GameLogEntry, i: number) => (
                          <tr key={i}>
                            <td className="day-col">{log.game_day}</td>
                            {isPitcher ? (
                              <>
                                <td>{(log.innings_pitched || 0).toFixed(1)}</td>
                                <td>{log.hits_allowed}</td>
                                <td>{log.runs_allowed}</td>
                                <td>{log.earned_runs}</td>
                                <td>{log.walks_allowed}</td>
                                <td>{log.strikeouts_pitched}</td>
                                <td>{log.home_runs_allowed}</td>
                                <td>{log.pitches_thrown || '-'}</td>
                                <td className="dec-col">
                                  {log.win ? <span className="dec-w">W</span> : log.loss ? <span className="dec-l">L</span> : log.save ? <span className="dec-sv">SV</span> : '-'}
                                </td>
                              </>
                            ) : (
                              <>
                                <td>{log.at_bats}</td>
                                <td>{log.runs}</td>
                                <td>{log.hits}</td>
                                <td>{log.doubles}</td>
                                <td>{log.triples}</td>
                                <td>{log.home_runs}</td>
                                <td>{log.rbi}</td>
                                <td>{log.walks}</td>
                                <td>{log.strikeouts}</td>
                                <td>{log.stolen_bases}</td>
                              </>
                            )}
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}
              </div>
            )}

            {!batting && !pitching && (!player.allSeasonStats || player.allSeasonStats.length === 0) && gameLogs.length === 0 && (
              <p className="no-data">No stats recorded yet. Stats appear after games are simulated.</p>
            )}
          </div>
        )}

        {/* ===== ATTRIBUTES TAB ===== */}
        {activeTab === 'attributes' && (
          <div className="attributes-tab">
            {attrs && Object.keys(attrs).length > 0 && (
              <div className="stat-table-section">
                <h4 className="section-title">Current Ratings</h4>
                <table className="attr-detail-table">
                  <thead>
                    <tr>
                      <th>Attribute</th>
                      <th>Grade</th>
                      <th>Rating</th>
                      <th>Bar</th>
                    </tr>
                  </thead>
                  <tbody>
                    {Object.entries(attrs as Record<string, number>).filter(([_, v]) => v > 0).map(([key, value]) => (
                      <tr key={key}>
                        <td className="attr-name-cell">{key.replace(/([A-Z])/g, ' $1').trim()}</td>
                        <td className="attr-grade-cell">
                          <span style={{ color: getAttrColor(value) }}>{numberToGrade(value)}</span>
                        </td>
                        <td className="attr-bar-cell">
                          <div className="attr-bar-track">
                            <div className="attr-bar-fill" style={{ width: `${Math.min(100, value as number)}%`, background: getAttrColor(value as number) }} />
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}

            {player.growthPotential && Object.keys(player.growthPotential).length > 0 && (
              <div className="stat-table-section">
                <h4 className="section-title">Growth Potential</h4>
                <table className="attr-detail-table">
                  <thead>
                    <tr>
                      <th>Attribute</th>
                      <th>+Potential</th>
                      <th>Bar</th>
                    </tr>
                  </thead>
                  <tbody>
                    {Object.entries(player.growthPotential as Record<string, number>).filter(([_, v]) => v > 0).map(([key, value]) => (
                      <tr key={key}>
                        <td className="attr-name-cell">{key.replace(/([A-Z])/g, ' $1').trim()}</td>
                        <td className="attr-val-cell">{'+' + value}</td>
                        <td className="attr-bar-cell">
                          <div className="attr-bar-track">
                            <div className="attr-bar-fill potential" style={{ width: `${Math.min(100, value as number)}%` }} />
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}

            {player.hiddenAttributes && (
              <div className="stat-table-section">
                <h4 className="section-title">Hidden Attributes 🎲</h4>
                <p className="hidden-attrs-note">These affect simulation but are not visible to other owners.</p>
                <table className="attr-detail-table">
                  <thead>
                    <tr>
                      <th>Attribute</th>
                      <th>Grade</th>
                      <th>Rating</th>
                      <th>Bar</th>
                    </tr>
                  </thead>
                  <tbody>
                    {[
                      { key: 'clutch', label: 'Clutch', desc: 'Performance in pressure situations' },
                      { key: 'consistency', label: 'Consistency', desc: 'Game-to-game variance' },
                      { key: 'durability', label: 'Durability', desc: 'Resistance to fatigue/injury' },
                      { key: 'workEthic', label: 'Work Ethic', desc: 'Development speed' },
                      { key: 'leadership', label: 'Leadership', desc: 'Team morale boost' },
                      { key: 'intelligence', label: 'Intelligence', desc: 'Game awareness' },
                    ].map(({ key, label }) => {
                      const val = (player.hiddenAttributes as any)?.[key] || 50;
                      return (
                        <tr key={key}>
                          <td className="attr-name-cell">{label}</td>
                          <td className="attr-grade-cell">
                            <span style={{ color: getAttrColor(val) }}>{numberToGrade(val)}</span>
                          </td>
                          <td className="attr-bar-cell">
                            <div className="attr-bar-track">
                              <div className="attr-bar-fill hidden" style={{ width: `${val}%`, background: getAttrColor(val) }} />
                            </div>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        )}

        {/* ===== PLAYSTYLE TAB ===== */}
        {activeTab === 'playstyle' && (
          <PlaystyleTab player={player} isOwner={isOwner} />
        )}

        {/* ===== EQUIPMENT TAB ===== */}
        {activeTab === 'equipment' && (
          <div className="equipment-tab">
            {ballsBalance > 0 && (
              <div className="equip-balance">Balance: <strong>{ballsBalance.toLocaleString()} $BALLS</strong></div>
            )}
            <div className="equip-slots">
              {['hat', 'glove', 'sunglasses'].map(slot => {
                const info = SLOT_INFO[slot];
                const equipped = (player as any).equipment?.[slot];
                return (
                  <div
                    key={slot}
                    className={`equip-slot ${equipped ? 'equipped' : 'empty'}`}
                    onClick={() => setEquipModal({ slot })}
                  >
                    <span className="equip-slot-icon">{info.icon}</span>
                    <span className="equip-slot-label">{info.label}</span>
                    {equipped ? (
                      <span className="equip-slot-item">+{equipped.boost} {equipped.attribute}</span>
                    ) : (
                      <span className="equip-slot-empty">Click to attach</span>
                    )}
                  </div>
                );
              })}
            </div>
          </div>
        )}

        {/* ===== EQUIPMENT MODAL ===== */}
        {equipModal && (() => {
          const slot = equipModal.slot;
          const info = SLOT_INFO[slot];
          const options = isPitcher ? PITCHER_OPTIONS : BATTER_OPTIONS;
          const currentEquip = (player as any).equipment?.[slot];
          const currentCost = currentEquip ? (BOOST_TIERS.find(t => t.boost === currentEquip.boost)?.cost || 0) : 0;
          return (
            <div className="equip-modal-overlay" onClick={() => setEquipModal(null)}>
              <div className="equip-modal" onClick={e => e.stopPropagation()}>
                <div className="equip-modal-header">
                  <h3>{info.icon} {currentEquip ? 'Upgrade' : 'Attach'} {info.label}</h3>
                  {currentEquip && (
                    <span className="equip-current-label">Current: +{currentEquip.boost} {currentEquip.attribute}</span>
                  )}
                  <button className="equip-modal-close" onClick={() => setEquipModal(null)}>×</button>
                </div>
                <div className="equip-modal-body">
                  <table className="equip-options-table">
                    <thead>
                      <tr>
                        <th>Attribute</th>
                        {BOOST_TIERS.map(tier => (
                          <th key={tier.boost}>{tier.label}</th>
                        ))}
                      </tr>
                    </thead>
                    <tbody>
                      {options.map(opt => (
                        <tr key={opt.attr}>
                          <td className="equip-attr-name">
                            <span className="equip-attr-icon">{opt.icon}</span>
                            {opt.label}
                          </td>
                          {BOOST_TIERS.map(tier => {
                            const upgradeCost = Math.max(0, tier.cost - currentCost);
                            const isSameItem = currentEquip?.itemId === `${slot}_${tier.boost}`;
                            return (
                              <td key={tier.boost} className="equip-price-cell">
                                <button
                                  className={`equip-buy-btn ${isSameItem ? 'equipped' : ''}`}
                                  disabled={isSameItem}
                                  onClick={async () => {
                                    if (!currentUserId) {
                                      alert('Please sign in first.');
                                      return;
                                    }
                                    const itemId = `${slot}_${tier.boost}`;
                                    try {
                                      const { supabase: sb } = await import('../services/supabase');
                                      const { data: { session } } = await sb.auth.getSession();
                                      const res = await fetch(`${FUNCTIONS_URL}/equip`, {
                                        method: 'POST',
                                        headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${session?.access_token}` },
                                        body: JSON.stringify({ action: 'equip', userId: currentUserId, playerId: player.id, itemId, attribute: opt.attr }),
                                      });
                                      const data = await res.json();
                                      if (data.success) {
                                        // Update player locally
                                        (player as any).equipment = { ...((player as any).equipment || {}), [slot]: { itemId, attribute: opt.attr, boost: tier.boost } };
                                        setPlayer({ ...player });
                                        const costMsg = data.isUpgrade 
                                          ? `${upgradeCost.toLocaleString()} $BALLS (upgrade)`
                                          : `${tier.cost.toLocaleString()} $BALLS`;
                                        alert(`✅ Equipped ${info.label} +${tier.boost} ${opt.label} for ${costMsg}!`);
                                        setEquipModal(null);
                                      } else {
                                        alert(data.error || 'Purchase failed');
                                      }
                                    } catch { alert('Purchase failed. Please try again.'); }
                                  }}
                                >
                                  {isSameItem ? (
                                    <span className="equip-equipped-label">Equipped</span>
                                  ) : (
                                    <>
                                      <span className="equip-boost">+{tier.boost}</span>
                                      <span className="equip-cost">{currentEquip ? upgradeCost.toLocaleString() : tier.cost} $BALLS{currentEquip && upgradeCost < tier.cost ? ' ↑' : ''}</span>
                                    </>
                                  )}
                                </button>
                              </td>
                            );
                          })}
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            </div>
          );
        })()}

        {/* ===== AWARDS TAB ===== */}
        {activeTab === 'awards' && (
          <div className="awards-tab">
            <p className="no-data">Awards are calculated at the end of each season.</p>
          </div>
        )}

        {/* ===== BLOODLINE TAB ===== */}
        {activeTab === 'bloodline' && (
          <div className="bloodline-tab">
            <div className="bloodline-header">
              <span className="generation-badge">Gen {player.dna?.generation || 1}</span>
              {/* Rarity badge removed — all players equal */}

            </div>

            {(player as any).nft_parent1_url || (player as any).nft_parent2_url ? (
              <div className="ancestry-tree">
                <div className="ancestor-level depth-0">
                  <h4>🧬 Gen0 NFT Parents</h4>
                  <div className="ancestor-row">
                    {(player as any).nft_parent1_url && (
                      <NftAncestorCard
                        imageUrl={(player as any).nft_parent1_url}
                        nftId={(player as any).nft_parent1_id}
                      />
                    )}
                    {(player as any).nft_parent2_url && (
                      <NftAncestorCard
                        imageUrl={(player as any).nft_parent2_url}
                        nftId={(player as any).nft_parent2_id}
                      />
                    )}
                  </div>
                </div>
              </div>
            ) : player.dna?.parent1Id || player.dna?.parent2Id || (player as any).parent1_id || (player as any).parent2_id ? (
              <div className="ancestry-tree">
                <BloodlineTree
                  parent1Id={player.dna?.parent1Id || (player as any).parent1_id}
                  parent2Id={player.dna?.parent2Id || (player as any).parent2_id}
                  depth={0}
                  maxDepth={2}
                />
              </div>
            ) : (
              <div className="no-ancestry">
                <p>🏅 <strong>Founding Generation</strong></p>
                <p>Original player — no bloodline ancestors. Can be bred to start a new line.</p>
              </div>
            )}

            <div className="breeding-info">
              <h4>Breeding Rules</h4>
              <ul>
                <li>Cannot breed within 3 generations</li>
                <li>Max 5 breeds per season</li>
                <li>10 forge seasons after retirement → Hall of Fame</li>
              </ul>
            </div>
          </div>
        )}

        {/* ===== CONTRACTS TAB ===== */}
        {activeTab === 'contracts' && (
          <div className="contracts-tab">
            {/* ASSIGN TO TEAM — visible when player is a FA (with or without a league) */}
            {!(player as any).team_id && assignTeams.length > 0 && (
              <div style={{ marginBottom: 24, background: 'rgba(212,160,23,0.06)', border: '1px solid rgba(212,160,23,0.2)', borderRadius: 10, padding: 20 }}>
                <h3 style={{ color: '#e8b82a', marginTop: 0, fontSize: '1rem' }}>⚾ Assign to Team</h3>
                <p style={{ color: '#9ca3af', fontSize: '0.85rem', marginBottom: 12 }}>Place {(player as any).first_name} on one of your teams. If the roster is full, the lowest scrub will be released automatically.</p>
                <select
                  value={assignSelectedTeam}
                  onChange={e => { setAssignSelectedTeam(e.target.value); setAssignMsg(''); }}
                  style={{ width: '100%', padding: '10px 12px', background: '#0d1117', color: '#e8e8e8', border: '1px solid #2d3748', borderRadius: 8, fontSize: 14, marginBottom: 12 }}
                >
                  <option value="">Select a team...</option>
                  {assignTeams.map(t => {
                    const leagueName = t.leagues ? `${t.leagues.name} (${t.leagues.season_year})` : '';
                    const owned = t.owner_id === (currentUserId || '');
                    return (
                      <option key={t.id} value={t.id}>
                        {t.city} {t.name}{t.is_ai ? ' 🤖' : ''}{owned ? ' ⭐' : ''}{leagueName ? ` — ${leagueName}` : ''}
                      </option>
                    );
                  })}
                </select>

                {assignMsg && <p style={{ color: assignMsg.includes('✅') ? '#22c55e' : '#ef4444', fontSize: 13, marginBottom: 8 }}>{assignMsg}</p>}

                {assignSelectedTeam && (
                  <button
                    disabled={assignLoading}
                    onClick={async () => {
                      if (!player) return;
                      const { supabase, isSupabaseConfigured } = await import('../services/supabase');
                      if (!isSupabaseConfigured() || !supabase) return;
                      const { data: { session } } = await supabase.auth.getSession();
                      if (!session) { setAssignMsg('Not signed in'); return; }
                      setAssignLoading(true);
                      setAssignMsg('Assigning...');
                      try {
                        const res = await fetch(`${FUNCTIONS_URL}/contract-offer`, {
                          method: 'POST',
                          headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${session.access_token}` },
                          body: JSON.stringify({ action: 'assign-player-to-team', playerId: actualPlayerId, teamId: assignSelectedTeam }),
                        });
                        const data = await res.json();
                        if (data.success) {
                          setAssignMsg(`✅ ${(player as any).first_name} assigned to ${data.teamName}!${data.droppedScrub ? ` ${data.droppedScrub}` : ''}`);
                          setTimeout(() => window.location.reload(), 1500);
                        } else {
                          setAssignMsg(data.error || 'Assignment failed');
                        }
                      } catch (err: any) {
                        setAssignMsg(err.message || 'Network error');
                      } finally {
                        setAssignLoading(false);
                      }
                    }}
                    style={{ width: '100%', padding: '12px', background: assignLoading ? '#374151' : 'linear-gradient(135deg, #d4a017, #e8b82a)', color: assignLoading ? '#6b7280' : '#060a12', border: 'none', borderRadius: 8, fontWeight: 700, fontSize: 14, cursor: assignLoading ? 'wait' : 'pointer' }}
                  >
                    {assignLoading ? 'Assigning...' : 'Assign Player'}
                  </button>
                )}
              </div>
            )}

            <h3>📋 Contract Offers</h3>
            {loadingOffers ? (
              <p>Loading offers...</p>
            ) : contractOffers.length === 0 ? (
              <p className="no-data">No pending contract offers.</p>
            ) : (
              <div className="offers-list">
                {contractOffers.map((offer) => (
                  <div key={offer.id} className="offer-card">
                    <div className="offer-header">
                      <span className="team-name">{offer.team_city} {offer.team_name}</span>
                      <span className={`tier-badge tier-${offer.tier}`}>
                        {['Premier', 'Major', 'AAA', 'AA', 'Rookie'][offer.tier - 1] || 'Tier ' + offer.tier}
                      </span>
                    </div>
                    <div className="offer-details">
                      <div className="offer-stat">
                        <span className="label">Duration:</span>
                        <span className="value">{offer.duration} season{offer.duration > 1 ? 's' : ''}</span>
                      </div>
                    </div>
                    <div className="scout-report">
                      <p>"{offer.scout_report}"</p>
                    </div>
                    <div className="offer-actions">
                      <button 
                        className="accept-btn"
                        onClick={async () => {
                          const { supabase, isSupabaseConfigured } = await import('../services/supabase');
                          if (!isSupabaseConfigured() || !supabase) return;
                          
                          if (!confirm(`Accept contract from ${offer.team_city} ${offer.team_name}?`)) return;
                          
                          const { data: { session } } = await supabase.auth.getSession();
                          if (!session) { alert('Not signed in'); return; }
                          
                          const res = await fetch(`${FUNCTIONS_URL}/contract-offer`, {
                            method: 'POST',
                            headers: {
                              'Authorization': `Bearer ${session.access_token}`,
                              'Content-Type': 'application/json',
                            },
                            body: JSON.stringify({ action: 'accept-offer', offerId: offer.id }),
                          });
                          
                          const data = await res.json();
                          if (!res.ok) {
                            alert(data.error || 'Failed to accept offer');
                            return;
                          }
                          
                          alert(`✅ ${data.playerName} signed with ${data.teamName}!`);
                          window.location.reload();
                        }}
                      >
                        Accept Contract
                      </button>
                      <button
                        className="decline-btn"
                        style={{ background: 'rgba(239,68,68,0.1)', color: '#ef4444', border: '1px solid rgba(239,68,68,0.25)', borderRadius: 6, padding: '8px 16px', marginLeft: 10, cursor: 'pointer', fontWeight: 600, fontSize: 13 }}
                        onClick={async () => {
                          const { supabase, isSupabaseConfigured } = await import('../services/supabase');
                          if (!isSupabaseConfigured() || !supabase) return;
                          if (!confirm(`Decline offer from ${offer.team_city} ${offer.team_name}?`)) return;
                          await supabase.from('contract_offers').update({ status: 'rejected' }).eq('id', offer.id);
                          window.location.reload();
                        }}
                      >
                        Decline
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}

        {activeTab === 'development' && (
          <DevelopmentTab playerId={player.id} isPitcher={isPitcher} />
        )}
      </div>

      {/* POST-BUY FLOW MODAL */}
      {postBuyStep && postBuyPlayerData && (
        <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.85)', zIndex: 1000, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
          <div style={{ background: '#0d1117', border: '1px solid #d4a017', borderRadius: 12, padding: 32, maxWidth: 480, width: '90%', color: '#e8e8e8' }} onClick={e => e.stopPropagation()}>

            {postBuyStep === 'rename' && (
              <>
                <div style={{ color: '#d4a017', fontSize: 12, marginBottom: 8 }}>Step 1 of 2</div>
                <h3 style={{ margin: '0 0 12px', color: '#e8b82a' }}>Name Your Player</h3>
                <p style={{ color: '#9ca3af', marginBottom: 20 }}>
                  You just bought <strong style={{ color: '#e8e8e8' }}>{postBuyPlayerData.originalFirst} {postBuyPlayerData.originalLast}</strong> (OVR {numberToGrade(postBuyPlayerData.overall)} {postBuyPlayerData.position}). Give them a new name or keep the original.
                </p>
                <div style={{ display: 'flex', gap: 12, marginBottom: 24 }}>
                  <div style={{ flex: 1 }}>
                    <label style={{ display: 'block', color: '#9ca3af', fontSize: 12, marginBottom: 6 }}>First Name</label>
                    <input style={{ width: '100%', background: '#1a1f2e', border: '1px solid #2a3040', borderRadius: 6, padding: '8px 12px', color: '#e8e8e8', fontSize: 14 }}
                      placeholder={postBuyPlayerData.first_name} value={renameFirst} onChange={e => setRenameFirst(e.target.value)} maxLength={20} />
                  </div>
                  <div style={{ flex: 1 }}>
                    <label style={{ display: 'block', color: '#9ca3af', fontSize: 12, marginBottom: 6 }}>Last Name</label>
                    <input style={{ width: '100%', background: '#1a1f2e', border: '1px solid #2a3040', borderRadius: 6, padding: '8px 12px', color: '#e8e8e8', fontSize: 14 }}
                      placeholder={postBuyPlayerData.last_name} value={renameLast} onChange={e => setRenameLast(e.target.value)} maxLength={20} />
                  </div>
                </div>
                <div style={{ display: 'flex', gap: 12, justifyContent: 'flex-end' }}>
                  <button style={{ background: 'transparent', border: '1px solid #2a3040', borderRadius: 6, padding: '10px 20px', color: '#9ca3af', cursor: 'pointer' }} onClick={closePostBuy}>Skip</button>
                  <button style={{ background: '#d4a017', border: 'none', borderRadius: 6, padding: '10px 20px', color: '#000', fontWeight: 700, cursor: 'pointer' }} disabled={renameSaving} onClick={handlePostBuyRename}>
                    {renameSaving ? 'Saving...' : 'Next — Pick Portrait'}
                  </button>
                </div>
              </>
            )}

            {postBuyStep === 'portraits' && (
              <>
                <div style={{ color: '#d4a017', fontSize: 12, marginBottom: 8 }}>Step 2 of 2</div>
                <h3 style={{ margin: '0 0 12px', color: '#e8b82a' }}>Choose a Portrait</h3>
                <p style={{ color: '#9ca3af', marginBottom: 20 }}>
                  Pick the look for <strong style={{ color: '#e8e8e8' }}>{postBuyPlayerData.first_name} {postBuyPlayerData.last_name}</strong>.
                  {portraitLoading ? ' Generating 3 options...' : ''}
                </p>
                {portraitLoading && (
                  <div style={{ textAlign: 'center', padding: '40px 0', color: '#9ca3af' }}>Generating portraits... (~15s)</div>
                )}
                {portraitError && (
                  <div style={{ color: '#ef4444', marginBottom: 16, fontSize: 13 }}>{portraitError}</div>
                )}
                {portraitChoices.length > 0 && (
                  <div style={{ display: 'flex', gap: 12, justifyContent: 'center', marginBottom: 24 }}>
                    {portraitChoices.map(choice => (
                      <div key={choice.slot} onClick={() => setSelectedPortrait(choice.slot)}
                        style={{ cursor: 'pointer', border: selectedPortrait === choice.slot ? '2px solid #d4a017' : '2px solid transparent', borderRadius: 8, overflow: 'hidden', flex: 1 }}>
                        <img src={choice.url} alt={`Portrait ${choice.slot}`} style={{ width: '100%', display: 'block' }} />
                      </div>
                    ))}
                  </div>
                )}
                <div style={{ display: 'flex', gap: 12, justifyContent: 'flex-end' }}>
                  <button style={{ background: 'transparent', border: '1px solid #2a3040', borderRadius: 6, padding: '10px 20px', color: '#9ca3af', cursor: 'pointer' }} onClick={() => {
                    // If portraits were generated but skipped, auto-use the first one
                    if (portraitChoices.length > 0) {
                      handleSelectPortrait(portraitChoices[0].slot);
                    } else {
                      setPostBuyStep('done');
                    }
                  }}>Skip Portrait</button>
                  <button style={{ background: '#d4a017', border: 'none', borderRadius: 6, padding: '10px 20px', color: '#000', fontWeight: 700, cursor: 'pointer' }}
                    disabled={selectedPortrait === null || portraitSaving}
                    onClick={() => selectedPortrait !== null && handleSelectPortrait(selectedPortrait)}>
                    {portraitSaving ? 'Saving...' : 'Use This Portrait'}
                  </button>
                </div>
              </>
            )}

            {postBuyStep === 'done' && (
              <>
                <h3 style={{ margin: '0 0 16px', color: '#e8b82a' }}>Player Acquired!</h3>
                <p style={{ color: '#9ca3af', marginBottom: 24 }}>
                  <strong style={{ color: '#e8e8e8' }}>{postBuyPlayerData.first_name} {postBuyPlayerData.last_name}</strong> is now yours. Manage them from your profile.
                </p>
                <div style={{ display: 'flex', justifyContent: 'flex-end' }}>
                  <button style={{ background: '#d4a017', border: 'none', borderRadius: 6, padding: '10px 24px', color: '#000', fontWeight: 700, cursor: 'pointer' }} onClick={closePostBuy}>Done</button>
                </div>
              </>
            )}

          </div>
        </div>
      )}

    </div>
  );
};
// NFT Gen0 ancestor card — displays gen0 NFT portrait with monster type
const NftAncestorCard: React.FC<{ imageUrl: string; nftId?: number }> = ({ imageUrl, nftId }) => {
  const [nftInfo, setNftInfo] = useState<{monster?: string; gender?: string} | null>(null);

  useEffect(() => {
    if (!nftId) return;
    (async () => {
      const { supabase, isSupabaseConfigured } = await import('../services/supabase');
      if (!isSupabaseConfigured() || !supabase) return;
      const { data } = await supabase.from('nft_images').select('monster,gender').eq('id', nftId).single();
      if (data) setNftInfo(data);
    })();
  }, [nftId]);

  const monster = nftInfo?.monster || 'Gen0';
  const genderEmoji = nftInfo?.gender === 'female' ? '♀' : '♂';

  return (
    <div className="ancestor-card nft-ancestor">
      <div className="ancestor-portrait">
        <img src={imageUrl} alt="" className="ancestor-avatar" 
          onError={e => { (e.target as HTMLImageElement).style.display = 'none'; }} />
        <span className="ancestor-status-badge nft-badge">✨</span>
      </div>
      <div className="ancestor-body">
        <div className="ancestor-name" style={{textTransform:'capitalize'}}>{monster}</div>
        <div className="ancestor-meta">
          <span className="ancestor-pos">Gen0 NFT</span>
          <span className={`ancestor-gender ${nftInfo?.gender === 'female' ? 'female' : 'male'}`}>{genderEmoji}</span>
        </div>
        <div className="ancestor-attrs">Founding bloodline</div>
      </div>
    </div>
  );
};

// BloodlineTree — recursive component showing up to 3 generations with portraits
const BloodlineTree: React.FC<{ parent1Id?: string; parent2Id?: string; depth: number; maxDepth: number }> = ({ parent1Id, parent2Id, depth, maxDepth }) => {
  const depthLabels = ['🧬 Parents', '👨‍👩‍👧 Grandparents', '🏠 Great-Grandparents'];
  return (
    <div className={`ancestor-level depth-${depth}`}>
      <h4>{depthLabels[depth] || 'Ancestors'}</h4>
      <div className="ancestor-row">
        {parent1Id && <AncestorCard playerId={parent1Id} depth={depth} maxDepth={maxDepth} />}
        {parent2Id && <AncestorCard playerId={parent2Id} depth={depth} maxDepth={maxDepth} />}
      </div>
    </div>
  );
};

const AncestorCard: React.FC<{ playerId: string; depth: number; maxDepth: number }> = ({ playerId, depth, maxDepth }) => {
  const [p, setP] = useState<any>(null);

  useEffect(() => { load(); }, [playerId]);

  async function load() {
    const { supabase, isSupabaseConfigured } = await import('../services/supabase');
    if (!isSupabaseConfigured() || !supabase) return;
    const { data } = await supabase.from('players')
      .select('id, first_name, last_name, position, overall, retired, is_deceased, gender, avatar_url, power, contact, speed, velocity, control, movement, parent1_id, parent2_id, generation')
      .eq('id', playerId)
      .single();
    setP(data);
  }

  if (!p) return <div className="ancestor-card loading"><span className="ancestor-shimmer"></span></div>;

  const isPitcher = p.position === 'P';
  const attrs = isPitcher
    ? `VEL ${p.velocity||0} · CTL ${p.control||0} · MOV ${p.movement||0}`
    : `PWR ${p.power||0} · CON ${p.contact||0} · SPD ${p.speed||0}`;

  const status = p.is_deceased ? '💀' : p.retired ? '👴' : '⚾';

  return (
    <div className="ancestor-card" onClick={() => window.dispatchEvent(new CustomEvent('navigate', { detail: `/player/${p.id}` }))}>
      <div className="ancestor-portrait">
        {p.avatar_url
          ? <img src={p.avatar_url} alt="" className="ancestor-avatar" />
          : <div className="ancestor-initials">{p.first_name?.[0]}{p.last_name?.[0]}</div>
        }
        <span className="ancestor-status-badge">{status}</span>
      </div>
      <div className="ancestor-body">
        <div className="ancestor-name">{p.first_name} {p.last_name}</div>
        <div className="ancestor-meta">
          <span className="ancestor-pos">{p.position}</span>
          <span className="ancestor-ovr">OVR {numberToGrade(p.overall)}</span>
          <span className={`ancestor-gender ${p.gender === 'female' ? 'female' : 'male'}`}>{p.gender === 'female' ? '♀' : '♂'}</span>
        </div>
        <div className="ancestor-attrs">{attrs}</div>
      </div>
      {depth < maxDepth && (p.parent1_id || p.parent2_id) && (
        <div className="ancestor-children">
          <BloodlineTree parent1Id={p.parent1_id} parent2Id={p.parent2_id} depth={depth + 1} maxDepth={maxDepth} />
        </div>
      )}
    </div>
  );
};

export default PlayerProfilePage;
// force deploy 1781477880
